import {
  normalizePathogenOption,
  type LastSampleInputData,
  type SampleFormData,
} from './sampleFormSchema';

const LAST_INPUT_DATA_KEY = 'last_sample_input_data';

/** 新規ごとに空にすべきキー（誤登録・シード値の持ち越し防止） */
const EPHEMERAL_LAST_INPUT_KEYS = [
  'name',
  'sampleCreatedDate',
  'notes',
  'disposalDate',
  'remainingQuantity',
] as const;

function omitEphemeralKeys(data: Record<string, unknown>): Record<string, unknown> {
  const next = { ...data };
  for (const key of EPHEMERAL_LAST_INPUT_KEYS) {
    delete next[key];
  }
  return next;
}

/** 新規登録フォームの前回入力（localStorage）を読む */
export function loadLastSampleInputData(): LastSampleInputData {
  try {
    const saved = localStorage.getItem(LAST_INPUT_DATA_KEY);
    if (!saved) return {};
    const data = omitEphemeralKeys(JSON.parse(saved) as Record<string, unknown>);
    return {
      sampleType: typeof data.sampleType === 'string' ? data.sampleType : '',
      bslLevel: typeof data.bslLevel === 'string' ? data.bslLevel : '',
      initialQuantity:
        data.initialQuantity !== undefined ? (data.initialQuantity as number | null) : null,
      storageDate: typeof data.storageDate === 'string' ? data.storageDate : '',
      storageRoomId:
        data.storageRoomId !== undefined ? (data.storageRoomId as number | undefined) : undefined,
      drawerNumber:
        data.drawerNumber !== undefined ? (data.drawerNumber as number | null) : null,
      caseName: typeof data.caseName === 'string' ? data.caseName : '',
      pathogenOption: normalizePathogenOption(data.pathogenOption),
      pathogenGenus: typeof data.pathogenGenus === 'string' ? data.pathogenGenus : '',
      pathogenSpecies: typeof data.pathogenSpecies === 'string' ? data.pathogenSpecies : '',
      pathogenSubspecies:
        typeof data.pathogenSubspecies === 'string' ? data.pathogenSubspecies : '',
      pathogenStrain: typeof data.pathogenStrain === 'string' ? data.pathogenStrain : '',
      pathogenNote: typeof data.pathogenNote === 'string' ? data.pathogenNote : '',
      drawerNote: typeof data.drawerNote === 'string' ? data.drawerNote : '',
    };
  } catch {
    return {};
  }
}

type SaveableLastInput = Partial<SampleFormData> & {
  pathogenOption?: string;
  sampleCreatedDate?: string;
};

/** 空文字以外をマージして前回入力を保存（補助機能のため失敗は無視） */
export function saveLastSampleInputData(data: SaveableLastInput): void {
  try {
    let merged: Record<string, unknown> = {};
    const existing = localStorage.getItem(LAST_INPUT_DATA_KEY);
    if (existing) {
      try {
        merged = JSON.parse(existing) as Record<string, unknown>;
      } catch {
        merged = {};
      }
    }

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) continue;
      if (typeof value === 'string' && value === '') continue;
      merged[key] = value;
    }

    localStorage.setItem(
      LAST_INPUT_DATA_KEY,
      JSON.stringify(omitEphemeralKeys(merged))
    );
  } catch {
    // 入力データの永続化は補助機能
  }
}
